import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../../hooks/useAuth';

const QUADRANTS = [
  { id: 'strengths', label: 'Strengths', headerBg: '#e8f5ee', headerColor: '#2d7a4f' },
  { id: 'weaknesses', label: 'Weaknesses', headerBg: '#fde8e8', headerColor: '#c0392b' },
  { id: 'opportunities', label: 'Opportunities', headerBg: '#e8eef8', headerColor: '#1e3a5f' },
  { id: 'threats', label: 'Threats', headerBg: '#fff8e1', headerColor: '#c9952a' },
];

function QuadrantInput({ quadrant, items, onChange }) {
  const [input, setInput] = useState('');

  function addItem() {
    const trimmed = input.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setInput('');
  }

  function removeItem(idx) {
    onChange(items.filter((_, i) => i !== idx));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  }

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{
        padding: '10px 14px',
        background: quadrant.headerBg,
        borderBottom: '1px solid var(--color-border)',
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: quadrant.headerColor }}>
          {quadrant.label}
        </span>
      </div>
      <div style={{ padding: 12 }}>
        {items.map((item, idx) => (
          <div key={idx} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginBottom: 6,
            padding: '6px 8px',
            background: 'var(--color-border-light)',
            borderRadius: 4,
          }}>
            <span style={{ fontSize: 13, flex: 1, lineHeight: 1.5 }}>{item}</span>
            <button
              onClick={() => removeItem(idx)}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
                fontSize: 14,
                padding: '0 2px',
                flexShrink: 0,
              }}
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, marginTop: items.length > 0 ? 8 : 0 }}>
          <input
            type="text"
            className="form-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Add ${quadrant.label.toLowerCase()} point...`}
            style={{ flex: 1, fontSize: 13 }}
          />
          <button
            onClick={addItem}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${quadrant.headerColor}`,
              background: quadrant.headerBg,
              color: quadrant.headerColor,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SWOTFramework({ dealId }) {
  const { user, userProfile } = useAuth();
  const [data, setData] = useState({
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: [],
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'deals', dealId, 'frameworks', 'swot'))
      .then((snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setData({
            strengths: d.strengths || [],
            weaknesses: d.weaknesses || [],
            opportunities: d.opportunities || [],
            threats: d.threats || [],
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealId]);

  function setQuadrant(id, items) {
    setData((prev) => ({ ...prev, [id]: items }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      await setDoc(doc(db, 'deals', dealId, 'frameworks', 'swot'), {
        completed_by: user?.uid || '',
        completed_by_name: userProfile?.full_name || userProfile?.display_name || '',
        completed_at: new Date().toISOString(),
        ...data,
      });
      setSaveMsg('Saved successfully');
    } catch (err) {
      setSaveMsg('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)', fontSize: 13 }}>Loading...</div>;
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
        Complete the SWOT analysis by adding bullet points to each quadrant.
        Press Enter or click Add after each point.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {QUADRANTS.map((q) => (
          <QuadrantInput
            key={q.id}
            quadrant={q}
            items={data[q.id]}
            onChange={(items) => setQuadrant(q.id, items)}
          />
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: '1px solid var(--color-accent)',
            background: 'var(--color-accent)',
            color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Framework'}
        </button>
        {saveMsg && (
          <span style={{
            fontSize: 13,
            color: saveMsg.includes('failed') ? 'var(--color-error)' : 'var(--color-success)',
          }}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
}
